// constants
import { StoreStorageSyncKeys } from 'components/mrp/mrp.consts';

// types
import { AppointmentPreview, AppointmentStore } from './appointmentStore.types';
import { AppointmentFormFieldEntity } from 'components/mrp/components/AppointmentForm/AppointmentForm.types';

// utils
import dayjs from 'dayjs';
import { createWithEqualityFn } from 'zustand/traditional';
import { persist } from 'zustand/middleware';
import {
  calculateDailyPreviews,
  calculateWeeklyPreviews
} from './appointmentStore.utils';

export const useAppointmentStore = createWithEqualityFn<AppointmentStore>()(
  persist(
    (set, get) => ({
      appointment: undefined,
      dirty: false,
      hasHydrated: false,
      preview: undefined,
      control: {
        setAppointmentData: (appointment) =>
          set((state) => {
            // Block overwriting opened appointments
            if (!!state.appointment) {
              return {
                ...state
              };
            }

            const newAppointmentData = appointment ?? {
              planEntities: {
                CLIENT: [],
                EMPLOYEE: [],
                RESOURCE: [],
                WORKPLACE: []
              }
            };

            const planningEntityKeys = Object.keys(
              appointment?.planEntities || {}
            );
            const planningEntityIdentifiers = planningEntityKeys.flatMap(
              (key) =>
                appointment?.planEntities[key] as AppointmentFormFieldEntity[]
            );
            const selectedEntityIds = planningEntityIdentifiers.map(
              (entity) => entity.entityId!
            );

            if (!!appointment) {
              state.control.setPreviewData({
                id: appointment.id!,
                start: appointment.start!,
                end: appointment.end!,
                entityIds: selectedEntityIds,
                recurrencePattern: appointment.recurrencePattern
              });
            }

            return {
              ...state,
              appointment: newAppointmentData,
              dirty: false,
              showCloseWarning: false,
              original: !!appointment?.id
                ? {
                    id: appointment.id!,
                    start: appointment.start!,
                    end: appointment.end!,
                    entityIds: selectedEntityIds,
                    planningEntityIdentifiers: planningEntityIdentifiers
                  }
                : undefined
            };
          }),
        setDirty: (isDirty) =>
          set((state) => {
            return {
              ...state,
              dirty: isDirty
            };
          }),
        closeAppointment: (forceClose) =>
          set((state) => {
            const allowClose = forceClose || !state.dirty;

            return {
              ...state,
              showCloseWarning: !allowClose,
              appointment: allowClose ? undefined : state.appointment,
              previewData: allowClose ? undefined : state.previewData,
              original: allowClose ? undefined : state.original
            };
          }),
        cancelCloseAppointment: () =>
          set((state) => {
            return {
              ...state,
              showCloseWarning: false
            };
          }),
        setPreviewData: (previewData) => {
          set((state) => {
            return {
              ...state,
              previewData
            };
          });
        },
        getPreviewData: (entityIds, start, to) => {
          const previewData = get().previewData;
          if (!previewData) return [];

          if (
            !entityIds.some(
              (entityId) => previewData.entityIds.indexOf(entityId) != -1
            )
          )
            return [];

          const startDate = dayjs(start).startOf('day');
          const endDate = dayjs(to).add(1, 'day').startOf('day');

          const appointmentStart = dayjs(previewData.start);
          const appointmentEnd = dayjs(previewData.end);

          const matchingPeriods: {
            previewStart: string;
            previewEnd: string;
          }[] = [];

          const durationInMinutes = appointmentEnd.diff(
            appointmentStart,
            'minutes'
          );

          if (previewData.recurrencePattern?.recurrenceRule == 'daily') {
            const recurrenceLimit =
              previewData.recurrencePattern.recurrenceRangeType == 'NO_END'
                ? endDate
                : dayjs(previewData.recurrencePattern.recurrenceEnd).isBefore(
                    endDate
                  )
                ? dayjs(previewData.recurrencePattern.recurrenceEnd)
                : endDate;

            const recurrences = calculateDailyPreviews(
              previewData.recurrencePattern.recurrenceInterval!,
              startDate,
              recurrenceLimit,
              appointmentStart,
              appointmentEnd,
              durationInMinutes
            );

            matchingPeriods.push(...recurrences);
          } else if (
            previewData.recurrencePattern?.recurrenceRule == 'weekly'
          ) {
            const recurrenceLimit =
              previewData.recurrencePattern.recurrenceRangeType == 'NO_END'
                ? endDate
                : dayjs(previewData.recurrencePattern.recurrenceEnd).isBefore(
                    endDate
                  )
                ? dayjs(previewData.recurrencePattern.recurrenceEnd)
                : endDate;

            const recurrences = calculateWeeklyPreviews(
              previewData.recurrencePattern.recurrenceInterval!,
              startDate,
              recurrenceLimit,
              appointmentStart,
              durationInMinutes,
              previewData.recurrencePattern.daysSelected!
            );

            matchingPeriods.push(...recurrences);
          } else if (
            startDate.isBefore(appointmentEnd) &&
            endDate.isAfter(appointmentStart)
          ) {
            matchingPeriods.push({
              previewStart: previewData.start!,
              previewEnd: previewData.end!
            });
          }

          return matchingPeriods.map(
            (x) =>
              ({
                start: x.previewStart,
                end: x.previewEnd,
                entityIds: previewData.entityIds,
                id: previewData.id
              }) satisfies AppointmentPreview
          );
        }
      }
    }),
    {
      name: StoreStorageSyncKeys.APPOINTMENT_STORE,
      partialize: (state) => ({
        appointment: state.appointment,
        dirty: state.dirty,
        previewData: state.previewData,
        original: state.original
      }),
      onRehydrateStorage: (state) => {
        state.hasHydrated = true;
      },
      version: 1
    }
  )
);
